import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import ProjectSearchSelect from '../components/ProjectSearchSelect';
import APIClient from '../api/axios';
import {
  FiPlus, FiEye, FiEdit2, FiTrash2, FiX,
  FiCalendar, FiUser, FiMapPin, FiDownload, FiCheckCircle,
  FiRefreshCw, FiClipboard, FiImage, FiInfo,
} from 'react-icons/fi';
import './QualityCheckMgmt.css';


const STATUS_CFG = {
  approved: { label: 'Approved', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
};

const ACTIVITY_CFG = {
  site_inspection: { label: 'Site Inspection', icon: '🔍', color: '#2563eb', bg: '#eff6ff', desc: 'Inspect completed work as per quality standards.', iconCmp: FiClipboard },
  rework_tracking: { label: 'Rework Tracking', icon: '🔧', color: '#7c3aed', bg: '#f5f3ff', desc: 'Re-verify items previously marked as rejected.', iconCmp: FiRefreshCw },
};
const ACTIVITY_KEYS = Object.keys(ACTIVITY_CFG);

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.approved;
  return <span className="qc-badge" style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}>{cfg.label}</span>;
}
function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
const SERVER_ORIGIN = (process.env.NODE_ENV === 'production'
  ? 'https://creativeglassworld.com'
  : 'http://localhost:9119');
export default function QualityCheckMgmt() {
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [checks, setChecks] = useState([]);
  const [activeTab, setActiveTab] = useState('site_inspection');
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editCheck, setEditCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [confirmState, setConfirmState] = useState(null); // { title, message, onConfirm, confirmLabel, alertOnly }

  useEffect(() => {
    const pid = new URLSearchParams(location.search).get('projectId');
    APIClient.get('/projects')
      .then(r => {
        setProjects(r.data);
        const target = pid ? r.data.find(p => p._id === pid) : null;
        setSelectedProject(target || r.data[0] || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    APIClient.get('/roles').then(r => setRoles(r.data)).catch(() => {});
    APIClient.get('/users').then(r => setUsers(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedProject) loadChecks(); }, [selectedProject, selectedDate]);

  function loadChecks() {
    APIClient.get('/quality-checks', {
      params: { project: selectedProject._id, date: selectedDate },
    }).then(r => {
      setChecks(r.data);
      if (r.data.length && !selectedCheck) setSelectedCheck(r.data[0]);
    }).catch(() => setChecks([]));
  }

  function deleteCheck(id) {
    setConfirmState({
      title: 'Delete Quality Check',
      message: 'Delete this quality check? This cannot be undone.',
      confirmLabel: 'Delete',
      onConfirm: () => doDeleteCheck(id),
    });
  }

  async function doDeleteCheck(id) {
    setConfirmState(null);
    try {
      await APIClient.delete(`/quality-checks/${id}`);
      setChecks(prev => prev.filter(c => c._id !== id));
      if (selectedCheck?._id === id) setSelectedCheck(null);
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  }

  async function updateStatus(id, status) {
    try {
      const r = await APIClient.put(`/quality-checks/${id}`, { status });
      setChecks(prev => prev.map(c => c._id === id ? r.data : c));
      if (selectedCheck?._id === id) setSelectedCheck(r.data);
      toast.success('Status updated');
    } catch { toast.error('Failed'); }
  }

  async function approveProject() {
    if (!selectedProject) return;
    try {
      await APIClient.post(`/quality-checks/${selectedProject._id}/approve-project`);
      toast.success('Project quality check approved');
      APIClient.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    } catch (err) {
      const rejectedChecks = err.response?.data?.rejectedChecks;
      if (rejectedChecks?.length) {
        setConfirmState({
          title: 'Cannot Approve Project',
          message: `${rejectedChecks.length} quality check(s) are still rejected:`,
          list: rejectedChecks.map(r => `${r.qcNumber} (${ACTIVITY_CFG[r.activity]?.label || r.activity})`),
          alertOnly: true,
        });
      } else {
        toast.error(err.response?.data?.message || 'Failed to approve project');
      }
    }
  }

  const stats = {
    approved: checks.filter(c => c.status === 'approved').length,
    rejected: checks.filter(c => c.status === 'rejected').length,
    total: checks.length,
  };

  // donut
  const donutTotal = stats.total || 1;
  const donutSlices = [
    { pct: stats.approved / donutTotal, color: '#16a34a' },
    { pct: stats.rejected / donutTotal, color: '#ef4444' },
  ];
  function buildDonut() {
    const R = 36, cx = 50, cy = 50;
    let paths = [], start = -Math.PI / 2;
    for (const s of donutSlices) {
      const angle = s.pct * 2 * Math.PI;
      if (angle < 0.01) continue;
      const end = start + angle;
      const x1 = cx + R * Math.cos(start), y1 = cy + R * Math.sin(start);
      const x2 = cx + R * Math.cos(end),   y2 = cy + R * Math.sin(end);
      paths.push(<path key={s.color} d={`M${cx},${cy} L${x1},${y1} A${R},${R} 0 ${angle > Math.PI ? 1 : 0},1 ${x2},${y2} Z`} fill={s.color} />);
      start = end;
    }
    return paths;
  }

  const tabChecks = activeTab === 'rework_tracking'
    ? checks.filter(c => c.activity === 'rework_tracking' || (c.activity === 'site_inspection' && c.status === 'rejected'))
    : checks.filter(c => c.activity === activeTab);

  return (
    <Layout>
      {loading && <PageLoader text="Loading quality checks..." />}
      <div className="qc-root">

        {/* ── Top controls ── */}
        <div className="qc-topbar">
          <div className="qc-proj-select-wrap">
            <ProjectSearchSelect
              projects={projects}
              value={selectedProject}
              onChange={setSelectedProject}
              objectMode={true}
              label="Select Project"
              placeholder="Choose project…"
            />
          </div>
          <div className="qc-date-wrap">
            <label>Select Date</label>
            <div className="qc-date-box">
              <FiCalendar size={14} />
              <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />
            </div>
          </div>
        </div>

        {/* ── Project Info Card ── */}
        {selectedProject && (
          <div className="qc-proj-card">
            <div className="qc-proj-img-placeholder"><FiImage size={28} color="#94a3b8" /></div>
            <div className="qc-proj-info">
              <div className="qc-proj-name-row">
                <h3 className="qc-proj-name">{selectedProject.projectId ? `${selectedProject.projectId} – ` : ''}{selectedProject.title}</h3>
                <StatusBadge status={selectedProject.status?.replace('-', '_') || 'in_progress'} />
                <button className="qc-btn-primary qc-approve-project-btn" onClick={approveProject}>
                  <FiCheckCircle size={13} /> Approve Project
                </button>
              </div>
              <div className="qc-proj-meta">
                <span><FiUser size={12}/> <b>Customer</b> {selectedProject.client?.name || '—'}</span>
                <span><FiUser size={12}/> <b>Supervisor</b> {selectedProject.supervisor || '—'}</span>
                <span><FiCalendar size={12}/> <b>Start Date</b> {fmt(selectedProject.startDate)}</span>
                <span><FiCalendar size={12}/> <b>Expected Completion</b> {fmt(selectedProject.endDate)}</span>
                <span><FiMapPin size={12}/> <b>Location</b> {selectedProject.location || '—'}</span>
              </div>
            </div>
            {/* Status Overview */}
            <div className="qc-overview">
              <h4>Quality Status Overview</h4>
              <div className="qc-overview-stats">
                <div className="qc-ov-card qc-ov-green">
                  <div className="qc-ov-icon">✅</div>
                  <div>
                    <p className="qc-ov-label">Approved</p>
                    <p className="qc-ov-num">{stats.approved}</p>
                    <p className="qc-ov-sub">Checks</p>
                  </div>
                </div>
                <div className="qc-ov-card qc-ov-red">
                  <div className="qc-ov-icon">❌</div>
                  <div>
                    <p className="qc-ov-label">Rejected</p>
                    <p className="qc-ov-num">{stats.rejected}</p>
                    <p className="qc-ov-sub">Checks</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Donut */}
            <div className="qc-donut-wrap">
              <svg viewBox="0 0 100 100" width="100" height="100">
                {stats.total === 0 ? <circle cx="50" cy="50" r="36" fill="#f1f5f9" /> : buildDonut()}
                <circle cx="50" cy="50" r="23" fill="#fff" />
                <text x="50" y="46" textAnchor="middle" fontSize="12" fontWeight="800" fill="#0f172a">{stats.total}</text>
                <text x="50" y="56" textAnchor="middle" fontSize="6" fill="#94a3b8">Total Checks</text>
              </svg>
              <div className="qc-donut-legend">
                {[
                  { color: '#16a34a', label: 'Approved', count: stats.approved },
                  { color: '#ef4444', label: 'Rejected', count: stats.rejected },
                ].map((l, i) => (
                  <div key={i} className="qc-legend-row">
                    <span className="qc-legend-dot" style={{ background: l.color }} />
                    <span>{l.label} ({l.count})</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Main 2-col ── */}
        <div className="qc-main-grid">
          {/* LEFT: checklist + detail */}
          <div className="qc-left-col">
            {/* Quality Check List with tabs */}
            <div className="qc-card">
              <div className="qc-card-header">
                <h3>Quality Check List</h3>
                <button className="qc-btn-primary" onClick={() => { setEditCheck(null); setShowModal(true); }}>
                  <FiPlus size={13} /> Add Quality Check
                </button>
              </div>
              {/* Activity Tabs */}
              <div className="qc-tabs">
                {ACTIVITY_KEYS.map((tab, i) => (
                  <button key={tab}
                    className={`qc-tab ${activeTab === tab ? 'qc-tab-active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    <span className="qc-tab-num">{i + 1}</span>
                    {ACTIVITY_CFG[tab].label}
                  </button>
                ))}
              </div>
              {/* Checks Table */}
              <div className="qc-table-wrap">
              <table className="qc-table">
                <thead>
                  <tr><th>Check ID</th><th>Activity</th><th>Area / Work</th><th>Checked On</th><th>Checked By</th><th>Status</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {tabChecks.length === 0 && <tr><td colSpan={7} className="qc-empty">No checks for this activity. Click "+ Add Quality Check" to get started.</td></tr>}
                  {tabChecks.map(c => (
                    <tr key={c._id} className={selectedCheck?._id === c._id ? 'qc-row-selected' : ''} onClick={() => setSelectedCheck(c)}>
                      <td className="qc-td-id">{c.qcNumber || '—'}</td>
                      <td>{ACTIVITY_CFG[c.activity]?.label || c.activity}</td>
                      <td>{c.areaWork || '—'}</td>
                      <td>
                        <span>{fmt(c.checkDate)}</span>
                        {c.checkTime && <span className="qc-time-tag">{c.checkTime}</span>}
                      </td>
                      <td>{c.checkedByName || c.checkedBy?.name || '—'}</td>
                      <td><StatusBadge status={c.status} /></td>
                      <td>
                        <div className="qc-row-actions" onClick={e => e.stopPropagation()}>
                          <button className="qc-icon-btn qc-view" onClick={() => { setSelectedCheck(c); document.querySelector('.qc-detail-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} title="View"><FiEye size={13}/></button>
                          <button className="qc-icon-btn" onClick={() => { setEditCheck(c); setShowModal(true); }} title="Edit"><FiEdit2 size={13}/></button>
                          <button className="qc-icon-btn qc-del" onClick={() => deleteCheck(c._id)} title="Delete"><FiTrash2 size={13}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>

            {/* Quality Check Detail */}
            {selectedCheck && (
              <div className="qc-detail-card">
                <h3 className="qc-detail-title">Quality Check Details</h3>
                <div className="qc-detail-grid">
                  {/* Meta */}
                  <div className="qc-detail-meta">
                    <div className="qc-meta-row"><span>Check ID</span><strong>{selectedCheck.qcNumber}</strong></div>
                    <div className="qc-meta-row"><span>Activity</span><strong>{ACTIVITY_CFG[selectedCheck.activity]?.label}</strong></div>
                    <div className="qc-meta-row"><span>Area / Work</span><strong>{selectedCheck.areaWork || '—'}</strong></div>
                    <div className="qc-meta-row"><span>Checked On</span><strong>{fmt(selectedCheck.checkDate)} {selectedCheck.checkTime}</strong></div>
                    <div className="qc-meta-row"><span>Checked By</span><strong>{selectedCheck.checkedByName || selectedCheck.checkedBy?.name || '—'}</strong></div>
                    {selectedCheck.observations && (
                      <div className="qc-meta-row qc-meta-full"><span>Remarks</span><strong>{selectedCheck.observations}</strong></div>
                    )}
                    <div className="qc-meta-row">
                      <span>Status</span>
                      <div className="qc-status-actions">
                        <StatusBadge status={selectedCheck.status} />
                        {selectedCheck.status !== 'approved' && (
                          <button className="qc-action-sm qc-approve" onClick={() => updateStatus(selectedCheck._id, 'approved')}>✅ Approve</button>
                        )}
                        {selectedCheck.status !== 'rejected' && (
                          <button className="qc-action-sm qc-reject" onClick={() => updateStatus(selectedCheck._id, 'rejected')}>❌ Reject</button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Site Photos */}
                  <div className="qc-detail-photos">
                    <h4 className="qc-detail-sub">Site Photos</h4>
                    <div className="qc-photos-grid">
                      {(selectedCheck.sitePhotos || []).length === 0 && (
                        <p className="qc-empty-sm">No photos uploaded</p>
                      )}
                      {(selectedCheck.sitePhotos || []).map((ph, i) => (
                        <a key={i} className="qc-photo-thumb" href={ph.startsWith('http') ? ph : `${SERVER_ORIGIN}${ph}`} target="_blank" rel="noopener noreferrer">
                          <img src={ph.startsWith('http') ? ph : `${SERVER_ORIGIN}${ph}`} alt="site" />
                        </a>
                      ))}
                    </div>
                    {/* Completion Evidence */}
                    {selectedCheck.completionEvidence?.name && (
                      <div className="qc-evidence">
                        <h4 className="qc-detail-sub">Completion Evidence</h4>
                        <div className="qc-evidence-row">
                          <div className="qc-evidence-icon">📄</div>
                          <div className="qc-evidence-info">
                            <p className="qc-evidence-name">{selectedCheck.completionEvidence.name}</p>
                            {selectedCheck.completionEvidence.uploadedAt && (
                              <p className="qc-evidence-date">Uploaded on {fmt(selectedCheck.completionEvidence.uploadedAt)}</p>
                            )}
                          </div>
                          <button className="qc-icon-btn"><FiDownload size={14}/></button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: Quality Activities + Status Guide */}
          <div className="qc-right-col">
            {/* Quality Activities */}
            <div className="qc-card">
              <h3 className="qc-card-h3">Quality Activities</h3>
              <div className="qc-activity-grid">
                {ACTIVITY_KEYS.map((key) => {
                  const a = ACTIVITY_CFG[key];
                  const Icon = a.iconCmp;
                  return (
                    <button key={key} className={`qc-activity-card ${activeTab === key ? 'qc-activity-active' : ''}`}
                      onClick={() => setActiveTab(key)}>
                      <div className="qc-activity-icon" style={{ color: a.color, background: a.bg }}><Icon /></div>
                      <div>
                        <p className="qc-activity-title">{a.label}</p>
                        <p className="qc-activity-desc">{a.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Status Guide */}
            <div className="qc-card">
              <h3 className="qc-card-h3">Status Guide</h3>
              <div className="qc-guide-list">
                {[
                  { icon: '✅', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', title: 'Approved', desc: 'Work meets quality standards.' },
                  { icon: '❌', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', title: 'Rejected', desc: 'Work does not meet quality standards — rework required.' },
                ].map((s, i) => (
                  <div key={i} className="qc-guide-item" style={{ borderColor: s.border, background: s.bg + '66' }}>
                    <span className="qc-guide-ico">{s.icon}</span>
                    <div>
                      <p className="qc-guide-title" style={{ color: s.color }}>{s.title}</p>
                      <p className="qc-guide-desc">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tip */}
        <div className="qc-tip">
          <FiInfo size={14} color="#2563eb" />
          Quality checks help ensure high standards, reduce rework and improve customer satisfaction.
        </div>

      </div>

      {showModal && (
        <QCModal
          check={editCheck}
          projectId={selectedProject?._id}
          date={selectedDate}
          roles={roles}
          users={users}
          onClose={() => { setShowModal(false); setEditCheck(null); }}
          onSaved={(c) => {
            setChecks(prev => editCheck ? prev.map(x => x._id === c._id ? c : x) : [c, ...prev]);
            setSelectedCheck(c);
            setShowModal(false); setEditCheck(null);
            toast.success(editCheck ? 'Updated' : 'Quality check added');
          }}
        />
      )}

      {confirmState && (
        <div className="qc-overlay" onClick={() => setConfirmState(null)}>
          <div className="qc-confirm-box" onClick={e => e.stopPropagation()}>
            <h4 className="qc-confirm-title">{confirmState.title}</h4>
            <p className="qc-confirm-msg">{confirmState.message}</p>
            {confirmState.list && (
              <ul className="qc-confirm-list">
                {confirmState.list.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            )}
            <div className="qc-confirm-actions">
              {confirmState.alertOnly ? (
                <button className="qc-btn-primary" onClick={() => setConfirmState(null)}>OK</button>
              ) : (
                <>
                  <button className="qc-btn-ghost" onClick={() => setConfirmState(null)}>Cancel</button>
                  <button className="qc-btn-danger" onClick={confirmState.onConfirm}>{confirmState.confirmLabel || 'Confirm'}</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

/* ── QC Modal ── */
function QCModal({ check, projectId, date, roles, users, onClose, onSaved }) {
  const [form, setForm] = useState({
    activity:     check?.activity     || 'site_inspection',
    areaWork:     check?.areaWork     || '',
    checkDate:    check?.checkDate?.split('T')[0] || date,
    checkTime:    check?.checkTime    || '',
    checkedByRole: check?.checkedBy?.role?._id || check?.checkedBy?.role || '',
    checkedBy:    check?.checkedBy?._id || check?.checkedBy || '',
    status:       check?.status       || 'approved',
    observations: check?.observations || '',
  });
  const [areaWorkParts] = useState(() => (check?.areaWork || '').split(' — '));
  const [mpCategory, setMpCategory] = useState(areaWorkParts[0] || '');
  const [materialPlan, setMaterialPlan] = useState(null);
  const [existingPhotos, setExistingPhotos] = useState(check?.sitePhotos || []);
  const [newPhotos, setNewPhotos] = useState([]); // File[]
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function onPhotosChange(e) {
    const files = Array.from(e.target.files || []);
    setNewPhotos(p => [...p, ...files]);
    e.target.value = '';
  }
  function removeExistingPhoto(idx) { setExistingPhotos(p => p.filter((_, i) => i !== idx)); }
  function removeNewPhoto(idx) { setNewPhotos(p => p.filter((_, i) => i !== idx)); }

  useEffect(() => {
    if (!projectId) return;
    APIClient.get(`/material-planning/${projectId}`).then(r => setMaterialPlan(r.data)).catch(() => setMaterialPlan(null));
  }, [projectId]);

  const mpCategoryOptions = (materialPlan?.categories || []).map(c => c.title);

  function applyMpSelection(nextCat) {
    if (!nextCat) return;
    setForm(f => ({ ...f, areaWork: nextCat }));
  }

  const siteRoles = roles.filter(r => r.staffType === 'site');
  const siteUsers = users.filter(u => (u.role?.staffType || 'office') === 'site');
  const usersForRole = form.checkedByRole
    ? siteUsers.filter(u => (u.role?._id || u.role) === form.checkedByRole)
    : siteUsers;

  async function save() {
    if (!form.areaWork.trim()) return toast.error('Area/Work is required');
    setSaving(true);
    try {
      const payload = {
        ...form,
        project: projectId,
        existingSitePhotos: JSON.stringify(existingPhotos),
      };
      delete payload.checkedByRole; // UI-only helper, not part of the QualityCheck schema

      const data = new FormData();
      Object.entries(payload).forEach(([k, v]) => { if (v !== undefined) data.append(k, v); });
      newPhotos.forEach(f => data.append('sitePhotos', f));

      const r = check
        ? await APIClient.put(`/quality-checks/${check._id}`, data)
        : await APIClient.post('/quality-checks', data);
      onSaved(r.data);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  }

  return (
    <div className="qc-overlay">
      <div className="qc-modal" onClick={e => e.stopPropagation()}>
        <div className="qc-modal-header">
          <h3>{check ? 'Edit Quality Check' : 'Add Quality Check'}</h3>
          <button className="qc-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="qc-modal-body">
              <div className="qc-mrow">
                <div className="qc-mgroup">
                  <label>Status</label>
                  <select value={form.status} onChange={e => set('status', e.target.value)}>
                    {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="qc-mrow">
                <div className="qc-mgroup">
                  <label>Category *</label>
                  <select
                    value={mpCategory}
                    onChange={e => {
                      const val = e.target.value;
                      setMpCategory(val);
                      applyMpSelection(val, '');
                    }}
                  >
                    <option value="">Select category…</option>
                    {mpCategoryOptions.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div className="qc-mrow">
                <div className="qc-mgroup">
                  <label>Check Date</label>
                  <input type="date" value={form.checkDate} onChange={e => set('checkDate', e.target.value)} />
                </div>
                <div className="qc-mgroup">
                  <label>Check Time</label>
                  <input type="time" value={form.checkTime} onChange={e => set('checkTime', e.target.value)} />
                </div>
              </div>
              <div className="qc-mrow">
                <div className="qc-mgroup">
                  <label>Checked By — Role</label>
                  <select value={form.checkedByRole} onChange={e => { set('checkedByRole', e.target.value); set('checkedBy', ''); }}>
                    <option value="">All Roles</option>
                    {siteRoles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                  </select>
                </div>
                <div className="qc-mgroup">
                  <label>Checked By — User</label>
                  <select value={form.checkedBy} onChange={e => set('checkedBy', e.target.value)}>
                    <option value="">Select user…</option>
                    {usersForRole.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="qc-mrow">
                <div className="qc-mgroup qc-mfull">
                  <label>Remarks / Observations</label>
                  <textarea rows={2} value={form.observations} onChange={e => set('observations', e.target.value)} />
                </div>
              </div>
              {/* Site Photos */}
              <div className="qc-mrow">
                <div className="qc-mgroup qc-mfull">
                  <label>Site Photos <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional, multiple allowed)</span></label>
                  <div className="qc-photo-upload-grid">
                    {existingPhotos.map((ph, i) => (
                      <div key={`ex-${i}`} className="qc-photo-upload-thumb">
                        <img src={ph.startsWith('http') ? ph : `${SERVER_ORIGIN}${ph}`} alt="site" />
                        <button type="button" className="qc-photo-remove-btn" onClick={() => removeExistingPhoto(i)}><FiX size={11}/></button>
                      </div>
                    ))}
                    {newPhotos.map((file, i) => (
                      <div key={`new-${i}`} className="qc-photo-upload-thumb">
                        <img src={URL.createObjectURL(file)} alt="new upload" />
                        <button type="button" className="qc-photo-remove-btn" onClick={() => removeNewPhoto(i)}><FiX size={11}/></button>
                      </div>
                    ))}
                    <label className="qc-photo-add-tile">
                      <input type="file" accept="image/*" multiple onChange={onPhotosChange} hidden />
                      <FiImage size={18} />
                      <span>Add Photos</span>
                    </label>
                  </div>
                </div>
              </div>
        </div>
        <div className="qc-modal-footer">
          <button className="qc-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="qc-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : check ? 'Update' : 'Add Check'}</button>
        </div>
      </div>
    </div>
  );
}
